<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AiProviderAPI from 'dashboard/api/aiProvider.js';

const { t } = useI18n();

const isLoading = ref(false);
const isSaving = ref(false);
const configured = ref(false);

const provider = ref('openai');
const endpoint = ref('');
const apiKey = ref('');
const model = ref('');

const PROVIDERS = [
  {
    id: 'openai',
    label: 'OpenAI',
    endpoint: 'https://api.openai.com/v1',
    models: ['gpt-4o', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  },
  {
    id: 'openrouter',
    label: 'OpenRouter',
    endpoint: 'https://openrouter.ai/api/v1',
    models: [
      'openai/gpt-4o',
      'openai/gpt-4.1',
      'openai/gpt-4.1-mini',
      'anthropic/claude-sonnet-4-5',
      'anthropic/claude-haiku-4-5',
      'google/gemini-flash-1.5',
      'meta-llama/llama-3.3-70b-instruct',
      'mistralai/mistral-large',
    ],
  },
  {
    id: 'custom',
    label: 'Custom',
    endpoint: '',
    models: [],
  },
];

const currentProvider = () => PROVIDERS.find(p => p.id === provider.value) || PROVIDERS[2];

const onProviderChange = () => {
  const p = currentProvider();
  endpoint.value = p.endpoint;
  if (p.models.length && !p.models.includes(model.value)) {
    model.value = p.models[0];
  }
};

const detectProvider = (ep) => {
  if (!ep) return 'openai';
  if (ep.includes('openrouter.ai')) return 'openrouter';
  if (ep.includes('api.openai.com')) return 'openai';
  return 'custom';
};

onMounted(async () => {
  isLoading.value = true;
  try {
    const { data } = await AiProviderAPI.get();
    configured.value = data.configured;
    endpoint.value = data.endpoint || 'https://api.openai.com/v1';
    model.value = data.model || '';
    apiKey.value = data.api_key || '';
    provider.value = detectProvider(data.endpoint);
  } catch {
    // ignore
  } finally {
    isLoading.value = false;
  }
});

const save = async () => {
  isSaving.value = true;
  try {
    await AiProviderAPI.update({
      endpoint: endpoint.value,
      api_key: apiKey.value,
      model: model.value,
    });
    configured.value = true;
    useAlert(t('AI_PROVIDER.SAVE_SUCCESS'));
  } catch {
    useAlert(t('AI_PROVIDER.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const clear = async () => {
  isSaving.value = true;
  try {
    await AiProviderAPI.update({ endpoint: '', api_key: ' ', model: '' });
    configured.value = false;
    endpoint.value = 'https://api.openai.com/v1';
    apiKey.value = '';
    model.value = '';
    provider.value = 'openai';
    useAlert(t('AI_PROVIDER.CLEAR_SUCCESS'));
  } catch {
    useAlert(t('AI_PROVIDER.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SettingsLayout :is-loading="isLoading" :loading-message="$t('AI_PROVIDER.LOADING')">
    <template #header>
      <BaseSettingsHeader
        :title="$t('AI_PROVIDER.TITLE')"
        :description="$t('AI_PROVIDER.DESCRIPTION')"
        feature-name="ai_provider"
      />
    </template>
    <template #body>
      <div class="max-w-2xl flex flex-col gap-6 py-4">
        <!-- Status badge -->
        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            :class="configured ? 'bg-green-100 text-green-700' : 'bg-n-alpha-3 text-n-slate-11'"
          >
            <span
              class="w-1.5 h-1.5 rounded-full"
              :class="configured ? 'bg-green-500' : 'bg-n-slate-8'"
            />
            {{ configured ? $t('AI_PROVIDER.STATUS_ACTIVE') : $t('AI_PROVIDER.STATUS_NOT_CONFIGURED') }}
          </span>
        </div>

        <!-- Provider selector -->
        <div class="flex flex-col gap-2">
          <label class="text-body-main font-medium text-n-slate-12">
            {{ $t('AI_PROVIDER.PROVIDER_LABEL') }}
          </label>
          <div class="flex gap-2 flex-wrap">
            <button
              v-for="p in PROVIDERS"
              :key="p.id"
              class="px-4 py-2 rounded-lg border text-sm font-medium transition-colors"
              :class="provider === p.id
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-weak bg-n-card text-n-slate-11 hover:border-n-slate-7'"
              @click="provider = p.id; onProviderChange()"
            >
              {{ p.label }}
            </button>
          </div>
        </div>

        <!-- Endpoint -->
        <div class="flex flex-col gap-2">
          <label class="text-body-main font-medium text-n-slate-12">
            {{ $t('AI_PROVIDER.ENDPOINT_LABEL') }}
          </label>
          <input
            v-model="endpoint"
            type="url"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-card text-n-slate-12 text-sm focus:outline-none focus:border-n-brand"
            :placeholder="$t('AI_PROVIDER.ENDPOINT_PLACEHOLDER')"
          />
          <p class="text-xs text-n-slate-11">{{ $t('AI_PROVIDER.ENDPOINT_HINT') }}</p>
        </div>

        <!-- API Key -->
        <div class="flex flex-col gap-2">
          <label class="text-body-main font-medium text-n-slate-12">
            {{ $t('AI_PROVIDER.API_KEY_LABEL') }}
          </label>
          <input
            v-model="apiKey"
            type="password"
            autocomplete="new-password"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-card text-n-slate-12 text-sm focus:outline-none focus:border-n-brand"
            :placeholder="configured ? $t('AI_PROVIDER.API_KEY_SAVED') : $t('AI_PROVIDER.API_KEY_PLACEHOLDER')"
          />
        </div>

        <!-- Model -->
        <div class="flex flex-col gap-2">
          <label class="text-body-main font-medium text-n-slate-12">
            {{ $t('AI_PROVIDER.MODEL_LABEL') }}
          </label>
          <input
            v-model="model"
            type="text"
            list="model-suggestions"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-card text-n-slate-12 text-sm focus:outline-none focus:border-n-brand"
            :placeholder="$t('AI_PROVIDER.MODEL_PLACEHOLDER')"
          />
          <datalist id="model-suggestions">
            <option
              v-for="m in currentProvider().models"
              :key="m"
              :value="m"
            />
          </datalist>
          <p class="text-xs text-n-slate-11">{{ $t('AI_PROVIDER.MODEL_HINT') }}</p>
        </div>

        <!-- Actions -->
        <div class="flex gap-3">
          <Button
            :label="$t('AI_PROVIDER.SAVE_BUTTON')"
            :is-loading="isSaving"
            @click="save"
          />
          <Button
            v-if="configured"
            :label="$t('AI_PROVIDER.CLEAR_BUTTON')"
            slate
            @click="clear"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
